import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { Loader2, Lock, Check, AlertCircle, StickyNote } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * AdminNotesCard — internal free-text notepad on an order.
 *
 * - Admin/super_admin only. Renders nothing for any other role, and the
 *   backend already scrubs the field from customer responses so there's
 *   nothing sensitive to leak client-side either.
 * - Autosaves 1.2s after the user stops typing (debounced). Shows a
 *   compact indicator: `Saving…` / `Saved` / `Retry` on failure.
 * - Displays who last saved the note and how long ago so admins can
 *   see a lightweight audit trail without a separate history view.
 *
 * Props:
 *  - order:     the full order object (must contain admin_notes* fields)
 *  - onUpdated: optional callback fired with the updated order after a
 *               successful save — used by OrderDetailPage to refresh
 *               "last edited by/at" without a full order refetch.
 */
export function AdminNotesCard({ order, onUpdated }) {
    const { user } = useAuth();
    const isInternal = user?.role === 'admin' || user?.role === 'super_admin';

    // NB: we mount the hooks unconditionally so React's rules-of-hooks
    // stay happy — the null-guard below decides whether to render.
    const [value, setValue] = useState(order?.admin_notes || '');
    const [status, setStatus] = useState('idle'); // idle | dirty | saving | saved | error
    const [meta, setMeta] = useState({
        by: order?.admin_notes_updated_by_name,
        by_email: order?.admin_notes_updated_by_email,
        at: order?.admin_notes_updated_at,
    });
    const timerRef = useRef(null);
    // Track the value the server last confirmed so we don't POST loops.
    const savedValueRef = useRef(order?.admin_notes || '');

    // Sync when a fresh order lands (e.g. after switching orders).
    useEffect(() => {
        setValue(order?.admin_notes || '');
        savedValueRef.current = order?.admin_notes || '';
        setMeta({
            by: order?.admin_notes_updated_by_name,
            by_email: order?.admin_notes_updated_by_email,
            at: order?.admin_notes_updated_at,
        });
        setStatus('idle');
    }, [order?.id]);

    const save = useCallback(async (next) => {
        const trimmed = (next || '').replace(/\s+$/, '');
        if (trimmed === savedValueRef.current) return; // nothing changed
        setStatus('saving');
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.patch(
                `${API_BASE}/api/orders/${order.id}/admin-notes`,
                { admin_notes: trimmed },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            savedValueRef.current = data.admin_notes || '';
            setMeta({
                by: data.admin_notes_updated_by_name,
                by_email: data.admin_notes_updated_by_email,
                at: data.admin_notes_updated_at,
            });
            setStatus('saved');
            onUpdated && onUpdated(data);
            // fade the "Saved" indicator after 2 s
            setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
        } catch (err) {
            setStatus('error');
            toast.error('Could not save admin notes — retry?');
        }
    }, [order?.id, onUpdated]);

    const onChange = (e) => {
        const next = e.target.value;
        setValue(next);
        setStatus('dirty');
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => save(next), 1200);
    };

    // Flush pending save on blur so an admin can Tab away confident it's stored.
    const onBlur = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            save(value);
        }
    };

    // Cleanup pending timer on unmount so we don't fire a stale save.
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    if (!isInternal || !order) return null;

    return (
        <Card
            className="border-amber-200 bg-amber-50/40"
            data-testid="admin-notes-card"
        >
            <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-slate-800">
                        <StickyNote className="h-4 w-4 text-amber-600" />
                        <h3 className="text-sm font-semibold">Admin Notes</h3>
                        <span
                            className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600"
                            title="Only visible to admins / staff"
                        >
                            <Lock className="h-2.5 w-2.5" /> Internal
                        </span>
                    </div>
                    <SaveIndicator status={status} />
                </div>

                <Textarea
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Anything worth knowing about this order — supplier hiccups, unusual specs, promises to the customer, …"
                    rows={4}
                    maxLength={10000}
                    className="resize-y bg-white"
                    data-testid="admin-notes-textarea"
                />

                <LastEdited meta={meta} />
            </div>
        </Card>
    );
}

function SaveIndicator({ status }) {
    if (status === 'saving') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="admin-notes-status">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
        );
    }
    if (status === 'saved') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid="admin-notes-status">
                <Check className="h-3 w-3" /> Saved
            </span>
        );
    }
    if (status === 'dirty') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid="admin-notes-status">
                Unsaved…
            </span>
        );
    }
    if (status === 'error') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-red-600" data-testid="admin-notes-status">
                <AlertCircle className="h-3 w-3" /> Save failed
            </span>
        );
    }
    return null;
}

function LastEdited({ meta }) {
    if (!meta?.at) {
        return (
            <p className="mt-2 text-[11px] text-slate-400">
                Autosaves as you type. Only admins can see this note.
            </p>
        );
    }
    return (
        <p className="mt-2 text-[11px] text-slate-500" data-testid="admin-notes-last-edited">
            Last edited by <span className="font-medium text-slate-700">{meta.by || meta.by_email || 'an admin'}</span>{' · '}
            <span title={meta.at}>{formatRelative(meta.at)}</span>
        </p>
    );
}

function formatRelative(iso) {
    try {
        const ts = new Date(iso).getTime();
        const now = Date.now();
        const diff = Math.max(0, now - ts);
        const min = 60 * 1000, hr = 60 * min, day = 24 * hr;
        if (diff < min) return 'just now';
        if (diff < hr) return `${Math.floor(diff / min)}m ago`;
        if (diff < day) return `${Math.floor(diff / hr)}h ago`;
        if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch (e) {
        return iso;
    }
}

export default AdminNotesCard;
